<template>
  <div :class="['whiteboard-wrapper', { 'expanded': expanded }]">
    <div class="whiteboard">
      <canvas
        :class="['drawing-board', drawTool]"
        :width="canvasSize.width"
        :height="canvasSize.height"
        @mousedown="handleDown"
        @mouseup="handleUp"
        @mouseout="handleOut"
        @mousemove="handleMove"
        @touchstart.prevent="(e) => handleDown(e, true)"
        @touchmove.prevent="(e) => handleMove(e, true)"
        @touchend.prevent="(e) => handleUp(e, true)"
        ref="drawingArea"
      />
      <video
        :srcObject.prop="screenShareStream"
        class="projector"
        autoplay
        muted
        playsinline
        ref="projector"
      />
      <audio :srcObject.prop="screenShareAudioStream" autoplay/>
      <whiteboard-control
        v-show="$deviceInfo.isMobile || !drawing"
        :drawState="drawTool"
        :thicknessState="strokeSize"
        :colorHex="pickerColor"
        :screenShareState="screenSharing"
        :expanded="expanded"
        @onClickPencil="setTool(drawTool === 'pencil' ? 'none' : 'pencil')"
        @onClickThickness="setStrokeSize"
        @onClickPickerColor="setColor"
        @onClickEraser="setTool(drawTool === 'eraser' ? 'none' : 'eraser')"
        @onClickTrash="trashConfirmModal = true"
        @onClickScreenShare="$emit('onClickScreenShare')"
        @onClickSnapshot="execSnapshot"
        @onClickDisplaySize="toggleExpanded"
      />
      <proa-modal
        :modal="trashConfirmModal"
        :title="$t('pages.room.trashConfirmModal.title')"
        @close="trashConfirmModal = false"
      >
        <span class="text-pre-wrap">{{ $t('pages.room.trashConfirmModal.body') }}</span>
        <template slot="footer">
          <proa-button
            @click.native="trashConfirmModal = false"
            class="default-button-width mr-2"
          >
            {{ $t('common.cancel') }}
          </proa-button>
          <proa-button
            @click.native="execClear"
            class="default-button-width"
          >
            {{ $t('common.ok') }}
          </proa-button>
          </template>
      </proa-modal>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import WhiteboardControl from '@/components/molecules/room/WhiteboardControl'
import { WHITEBOARD_BASE_SIZE } from '@/lib/mediasoup/constants'

export default {
  name: 'Whiteboard',
  components: {
    WhiteboardControl
  },
  computed: {
    canvasSize: () => {
      return {
        width: WHITEBOARD_BASE_SIZE.width,
        height: WHITEBOARD_BASE_SIZE.height
      }
    },
    ...mapGetters({
      displaying: 'roomWhiteboard/displaying',
      expanded: 'roomWhiteboard/expanded',
      screenSharing: 'roomWhiteboard/screenSharing',
      screenShareStream: 'roomWhiteboard/screenShareStream',
      screenShareAudioStream: 'roomWhiteboard/screenShareAudioStream',
      strokeSize: 'roomWhiteboard/strokeSize',
      drawing: 'roomWhiteboard/drawing',
      drawTool: 'roomWhiteboard/tool',
      pickerColor:  'roomWhiteboard/color',
      drawingPosition: 'roomWhiteboard/position',
      snapshotImage: 'roomWhiteboard/snapshotImage'
    })
  },
  data() {
    return {
      adjustMagnificationQueue: null,
      trashConfirmModal: false
    }
  },
  methods: {
    execSnapshot: function() {
      this.snapshotWhiteboard({
        whiteboardCanvas: this.$refs.drawingArea,
        projector: this.$refs.projector
      })
      const virtualLink = document.createElement('a')
      virtualLink.href = this.snapshotImage
      virtualLink.download = `snapshot.png`
      virtualLink.click()
    },
    execClear: function() {
      this.trashConfirmModal = false;
      this.clearWhiteboard();
      this.$emit('onClearWhiteboard')
    },
    eventPosition: function(e, touch) {
      let x, y

      if (touch) {
        const canvasRect = this.$refs.drawingArea.getBoundingClientRect()
        x = e.changedTouches[0].pageX - canvasRect.left
        y = e.changedTouches[0].pageY - canvasRect.top
      } else {
        x = e.offsetX
        y = e.offsetY
      }

      return {
        x: x,
        y: y
      }
    },
    handleDown: function(e, touch) {
      if (e.button == 2 || this.drawTool === 'none') return
      const { x, y } = this.eventPosition(e, touch)

      this.setDrawing({
        drawing: true,
        x: x,
        y: y
      })
    },
    handleUp: function(e) {
      if (e.button == 2 || this.drawTool === 'none') return
      this.setDrawing({ drawing: false })
    },
    handleOut: function() {
      if (this.drawing) {
        this.setDrawing({ drawing: false })
      }
    },
    handleMove: function(e, touch) {
      if (!this.drawing) return
      const { x, y } = this.eventPosition(e, touch)

      this.setNewPosition({
        x: x,
        y: y
      })
      this.toolAction()
    },
    adjustMagnification: function() {
      clearTimeout(this.adjustMagnificationQueue)
      this.adjustMagnificationQueue = setTimeout(() => {
        this.setMagnification({
          width: this.$refs.drawingArea.clientWidth,
          height: this.$refs.drawingArea.clientHeight
        })
      }, 600)
    },
    ...mapMutations({
      setContext: 'roomWhiteboard/setContext',
      setColor: 'roomWhiteboard/setColor',
      setTool: 'roomWhiteboard/setTool',
      setMagnification: 'roomWhiteboard/setMagnification',
      setDrawing: 'roomWhiteboard/setDrawing',
      setNewPosition: 'roomWhiteboard/setNewPosition',
      setStrokeSize: 'roomWhiteboard/setStrokeSize',
      resetWhiteboardModule: 'roomWhiteboard/resetModule'
    }),
    ...mapActions({
      toggleExpanded: 'roomWhiteboard/toggleExpanded',
      toolAction: 'roomWhiteboard/toolAction',
      remoteAction: 'roomWhiteboard/remoteAction',
      snapshotWhiteboard: 'roomWhiteboard/snapshot',
      clearWhiteboard: 'roomWhiteboard/clear'
    })
  },
  mounted() {
    this.setContext(this.$refs.drawingArea.getContext('2d'))
    window.addEventListener('resize', this.adjustMagnification)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.adjustMagnification)
  },
  watch: {
    displaying: function() {
      this.adjustMagnification()
    },
    expanded: function() {
      this.adjustMagnification()
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables";
@import "@/styles/mediaquery";

.whiteboard-wrapper {
  display: flex;
  width: 90%;
  height: 100%;

  &.expanded {
    width: 100%;
  }
}

.whiteboard {
  align-self: center;
  width: 100%;
  position: relative;
  display: flex;
  background-color: white;

  &:before {
    content: "";
    display: block;
    padding-top: 56.25%;
  }

  @include mq(md) {
    &:hover .whiteboard-control {
      opacity: 0.3;
      pointer-events: all;
    }
  }
}

.drawing-board, .projector {
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
}

.drawing-board {
  z-index: 2;
  background-color: transparent;

  &.eraser {
    cursor: url('/cursor/eraser.png') 14 14, crosshair;
  }

  &.pencil {
    cursor: url('/cursor/pencil.png') 0 0, crosshair;
  }
}

.projector {
  z-index: 1;
}
</style>
